import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import publicApi from "../api/publicApi";
import styles from "./BusRoutes.module.css";
import { useToast } from "../Components/ui/Toast.jsx";

const BusRoutes = () => {
  const { showToast } = useToast();
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState("");
  const [departureTerminal, setDepartureTerminal] = useState("");
  const [arrivalTerminal, setArrivalTerminal] = useState("");
  const [terminals, setTerminals] = useState([]);

  const navigate = useNavigate();

  const fetchTerminals = async () => {
    try {
      const response = await publicApi.get("/list_terminal");
      setTerminals(response.data);
    } catch (err) {

    }
  };

  // Filtra rutas que estén disponibles para reservar:
  // - No canceladas ni finalizadas
  // - No salieron hace más de 24 horas
  const isRouteAvailable = (route) => {
    if (!route.Departure_date) return false;
    if (route.Status === 'cancel' || route.Status === 'finished' || route.Status === 'completed') return false;

    const dateStr = route.Departure_date.slice(0, 10);
    const timeStr = route.Time_out || '00:00:00';
    const departure = new Date(`${dateStr}T${String(timeStr).slice(0, 8)}`);
    const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);

    return departure > oneDayAgo;
  };

  const fetchFilteredRoutes = async () => {
    setLoading(true);
    try {
      const response = await publicApi.get("/courses");

      let validRoutes = response.data.filter(isRouteAvailable);

      if (date) {
        validRoutes = validRoutes.filter((route) => {
          const routeDate = new Date(route.Departure_date).toISOString().slice(0, 10);
          return routeDate === date;
        });
      }

      if (departureTerminal) {
        validRoutes = validRoutes.filter((route) =>
          route.Origin?.toLowerCase().includes(departureTerminal.toLowerCase())
        );
      }

      if (arrivalTerminal) {
        validRoutes = validRoutes.filter((route) =>
          route.Destination?.toLowerCase().includes(arrivalTerminal.toLowerCase())
        );
      }

      setRoutes(validRoutes);
    } catch (err) {
      showToast('No se pudieron buscar rutas.', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = async () => {
    setDate("");
    setDepartureTerminal("");
    setArrivalTerminal("");
    setLoading(true);

    try {
      const response = await publicApi.get("/courses");
      setRoutes(response.data.filter(isRouteAvailable));
    } catch (err) {
      // silenciar
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (rawDate) => {
    if (!rawDate) return "";
    // rawDate es "YYYY-MM-DD" — parsear como local para evitar offset UTC
    const [year, month, day] = rawDate.slice(0, 10).split('-');
    return `${day}/${month}/${year}`;
  };

  const formatTime = (rawTime) => {
    if (!rawTime) return "";
    // Time_out viene como "HH:MM:SS" desde MySQL TIME column
    return rawTime.slice(0, 5);
  };

  const handleViewSeats = (route) => {
    navigate("/confirmacion", { state: { travelDetails: route } });
  };

  const [searchParams] = useSearchParams();

  useEffect(() => {
    const initializeFilters = async () => {
      await fetchTerminals();

      // Leer parámetros de búsqueda desde Home
      const origenParam = searchParams.get("origen");
      const destinoParam = searchParams.get("destino");
      const fechaParam = searchParams.get("fecha");
      const ciudadParam = searchParams.get("ciudad");

      // Si hay parámetros, aplicar filtros y buscar
      if (origenParam || destinoParam || fechaParam || ciudadParam) {
        if (origenParam) setDepartureTerminal(origenParam);
        if (destinoParam) setArrivalTerminal(destinoParam);
        if (fechaParam) setDate(fechaParam);
        if (ciudadParam) setDepartureTerminal(ciudadParam);

        // Buscar con los filtros aplicados
        setLoading(true);
        try {
          const response = await publicApi.get("/courses");

          let validRoutes = response.data.filter(isRouteAvailable);

          if (fechaParam) {
            validRoutes = validRoutes.filter((route) => {
              const routeDate = new Date(route.Departure_date).toISOString().slice(0, 10);
              return routeDate === fechaParam;
            });
          }

          const depTerminal = origenParam || ciudadParam;
          if (depTerminal) {
            validRoutes = validRoutes.filter((route) =>
              route.Origin?.toLowerCase().includes(depTerminal.toLowerCase())
            );
          }

          if (destinoParam) {
            validRoutes = validRoutes.filter((route) =>
              route.Destination?.toLowerCase().includes(destinoParam.toLowerCase())
            );
          }

          setRoutes(validRoutes);
        } catch (err) {
          // silenciar
        } finally {
          setLoading(false);
        }
      } else {
        // Si no hay parámetros, cargar todas las rutas futuras
        resetFilters();
      }
    };

    initializeFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const today = new Date().toLocaleDateString("en-CA");

  return (
    <div className={styles.page}>
      <h2 className={styles.title}>Selecciona la ruta</h2>

      {/*  Barra de búsqueda */}
      <div className={styles.filtersCard}>
        <div className={styles.filtersGrid}>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className={styles.input}
            min={today}
          />

          <select
            value={departureTerminal}
            onChange={(e) => setDepartureTerminal(e.target.value)}
            className={styles.input}
          >
            <option value="">Terminal de salida</option>
            {terminals.filter(t => t.name !== arrivalTerminal).map((t) => (
              <option key={t.id} value={t.name}>{t.name}</option>
            ))}
          </select>

          <select
            value={arrivalTerminal}
            onChange={(e) => setArrivalTerminal(e.target.value)}
            className={styles.input}
          >
            <option value="">Terminal de llegada</option>
            {terminals.filter(t => t.name !== departureTerminal).map((t) => (
              <option key={t.id} value={t.name}>{t.name}</option>
            ))}
          </select>

          <button className={styles.btnSecondary} onClick={resetFilters}>
            Limpiar
          </button>

          <button className={styles.btnPrimary} onClick={fetchFilteredRoutes}>
            Buscar
          </button>
        </div>
      </div>

      {/*  Lista de rutas */}
      {loading ? (
        <p className={styles.noRoutes}>Cargando rutas…</p>
      ) : routes.length === 0 ? (
        <p className={styles.noRoutes}>No hay rutas disponibles.</p>
      ) : (
        <div className={styles.routesList}>
          {routes.map((route) => (
            <div key={route.ID} className={styles.routeCard}>
              <div className={styles.routeMain}>
                <h3>{route.Origin} → {route.Destination}</h3>
                <p>Fecha: {formatDate(route.Departure_date)}</p>
                <p>Hora salida: {formatTime(route.Arrival_date)}</p>
              </div>

              <div className={styles.routeSide}>
                <p className={styles.price}>${route.Price}</p>

                <button
                  className={styles.btnPrimary}
                  onClick={() => handleViewSeats(route)}
                >
                  Ver sillas
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BusRoutes;





